import React, { useCallback, useEffect, useLayoutEffect, useState } from "react";
import { createPortal } from "react-dom";
import { SYM } from "../utils/textSymbols";
import "./ApprovalBell.css";

const PENDING_TYPES = new Set(["calendar", "todo", "grocery", "recipe", "school_schedule", "rotation_shift"]);

export const APPROVAL_BELL_SLOTS = {
  calendars: "approval-bell-slot-calendars",
  groceries: "approval-bell-slot-groceries",
  meals: "approval-bell-slot-meals",
  todoMobile: "approval-bell-slot-todo-mobile",
  todoDesktop: "approval-bell-slot-todo-desktop",
};

function countPending(data) {
  return (data?.requests || []).filter(
    (r) => r.status === "pending" && PENDING_TYPES.has(r.type)
  ).length;
}

function resolveSlotElement(slotId) {
  if (!slotId) return null;
  if (typeof slotId === "string") {
    return document.getElementById(slotId);
  }
  if (Array.isArray(slotId)) {
    for (const id of slotId) {
      const el = document.getElementById(id);
      if (el && el.offsetParent !== null) return el;
    }
    return null;
  }
  return null;
}

export default function ApprovalBell({ apiBase, onOpen, refreshKey = 0, visible = true, slotId }) {
  const [count, setCount] = useState(0);
  const [slotEl, setSlotEl] = useState(null);

  const fetchCount = useCallback(async () => {
    try {
      const res = await fetch(`${apiBase}/api/GetPendingApprovals`, { credentials: "include" });
      if (!res.ok) return;
      const data = await res.json();
      setCount(countPending(data));
    } catch {
      /* ignore fetch errors */
    }
  }, [apiBase]);

  useEffect(() => {
    if (!visible) return;
    fetchCount();
  }, [fetchCount, refreshKey, visible]);

  useLayoutEffect(() => {
    if (!visible || !slotId || count === 0) {
      setSlotEl(null);
      return undefined;
    }

    const updateSlot = () => {
      setSlotEl(resolveSlotElement(slotId));
    };

    updateSlot();
    const retryId = window.setTimeout(updateSlot, 0);
    window.addEventListener("resize", updateSlot);
    return () => {
      window.clearTimeout(retryId);
      window.removeEventListener("resize", updateSlot);
    };
  }, [visible, slotId, count, refreshKey]);

  if (!visible || count === 0 || !slotEl) return null;

  return createPortal(
    <button
      type="button"
      className="approval-bell-btn"
      onClick={onOpen}
      aria-label={`${count} pending approval${count === 1 ? "" : "s"}`}
      title="Pending approvals"
    >
      <span className="approval-bell-icon" aria-hidden="true">{SYM.bell}</span>
      <span className="approval-bell-dot" aria-hidden="true" />
    </button>,
    slotEl
  );
}
